import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { useQueryParamValue } from 'src/app/shared/hooks/location';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { PsfPromoteOrSharePageCombinedState, PsfPromoteOrSharePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PsfPromoteOrSharePageDataContextProvider } from './pageData';
import { PsfPromoteOrSharePageFcmContextProvider } from './pageFcm';
import { PsfPromoteOrSharePageFcmExperimentsContextProvider } from './pageFcmExperiments';

export type ErrorHandlerProps = { context: ErrorStateDetails<PsfPromoteOrSharePageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PsfPromoteOrSharePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function PsfPromoteOrSharePageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);
	const sourceLocation = useQueryParamValue('source_location');

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageFcm, pageFcmExperiments } = pageContextState;

	return (
		<PsfPromoteOrSharePageDataContextProvider pageData={pageData}>
			<PsfPromoteOrSharePageFcmContextProvider pageFcm={pageFcm}>
				<PsfPromoteOrSharePageFcmExperimentsContextProvider pageFcmExperiments={pageFcmExperiments}>
					<TrackingContext
						properties={{
							petition_id: `petition_${pageData.data.petition.id}`,
							combo_bandit_variant: pageData.data.bandits.psfCombo.variant?.variantName,
							image: !!pageData.data.petition.photo,
							signature_count: pageData.data.petition.signatureState.signatureCount.displayed,
							source_location: sourceLocation,
						}}
					>
						{children}
					</TrackingContext>
				</PsfPromoteOrSharePageFcmExperimentsContextProvider>
			</PsfPromoteOrSharePageFcmContextProvider>
		</PsfPromoteOrSharePageDataContextProvider>
	);
}
